import { defineStore } from "pinia";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";
import { sampleRestClient, modelToApiMapping } from "@/api";

import { projectClientRest } from "@/api/clients/projectClient";
import { initBimdataAuth } from "@/setup/bimdataClient";

import db from "@/db";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { buildQueryset } from "@/utils/queryset";
import { isTransientFailure, logRequestFailure } from "@/utils/network";
dayjs.extend(utc);

import crudGenerator from "@/api/utils/offlineSync";
import { SYNC_REQUEST } from "@/api/utils/requestTimeouts";
import { OfflineError } from "@/utils/errors";

import { VITE_APP_ATMO_SERVICE_ID } from "@/env";

import { camelToSnakeCase, sortByOrdering } from "@/utils/helpers";

import { useRolesStore } from "@/store/modules/roles";
import { useInstallationsStore } from "@/store/modules/installations";
import { useSamplesStore } from "@/store/modules/samples";
import { usePhasesStore } from "@/store/modules/phases";
import { useSumStore } from "@/store/modules/sum";
import { useVlesStore } from "@/store/modules/vles";
import { useFlowsStore } from "@/store/modules/flows";
import { useDocumentsStore } from "@/store/modules/documents";
import { useConfigurationLinesStore } from "@/store/modules/configurationLines";
import { useContributorsStore } from "@/store/modules/contributors";
import { useSampleFlasksStore } from "@/store/modules/sampleFlasks";
import { useSampleFlaskPrepsStore } from "@/store/modules/sampleFlaskPrep";
import { useOrderFormsStore } from "@/store/modules/orderForms";
import { useRecordBooksStore } from "@/store/modules/recordBooks";
import { useReportTemplatesStore } from "@/store/modules/reportTemplates";
import { useAnalysisResultStore } from "@/store/modules/analysisResult";
import { useAcquisitionRecordBooksStore } from "@/store/modules/acquisitionRecordBooks";
import { isPendingRecord, recordKey, referencesAny } from "@/db/pendingRecord";
import {
  failTracked,
  pendingRecords,
  rejectTracked,
  unmarkRecords,
} from "@/db/pendingSync";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import { recipeFormFields } from "@/store/utils/recordbooks";
import { SYNC_STEPS, markStep } from "@/composables/network/syncProgress";

const { innerUpdate, resyncToBack: syncProject } = crudGenerator(
  db.models.Project
);
const { resyncToBack: syncClient } = crudGenerator(db.models.Client);
const { resyncToBack: syncInstallations } = crudGenerator(
  db.models.Installation
);
const { resyncToBack: syncConfigurationLines } = crudGenerator(
  db.models.ConfigurationLine
);
const { resyncToBack: syncSums } = crudGenerator(db.models.Sum);
const { resyncToBack: syncVles } = crudGenerator(db.models.Vle);
const { resyncToBack: syncFlows } = crudGenerator(db.models.Flow);
const { resyncToBack: syncFlasks } = crudGenerator(db.models.SampleFlask);
const { resyncToBack: syncPhases } = crudGenerator(db.models.Phase);
const {
  resyncToBack: syncRecordBooks,
  createSkeletonsOnBack: createRecordBookSkeletons,
} = crudGenerator(db.models.RecordBook);
const { resyncToBack: syncUpdatedSamples } = crudGenerator(db.models.Sample);
const { resyncToBack: syncAcquisitionDrafts } = crudGenerator(
  db.models.AcquisitionRecordBook
);

const createSamples = async (created, forms, blocked) => {
  const uuids = created.map(({ uuid }) => uuid);
  const markAll = (step) =>
    uuids.forEach((uuid) => markStep(recordKey("samples", uuid), step));
  try {
    markAll(SYNC_STEPS.SENDING);
    await sampleRestClient.bulkCreate(forms, SYNC_REQUEST);
    markAll(SYNC_STEPS.SENT);
  } catch (error) {
    markAll(SYNC_STEPS.WAITING);
    uuids.forEach((uuid) => blocked.add(uuid));
    if (await rejectTracked("samples", uuids, error)) return;
    await failTracked("samples", uuids, error);
    if (isTransientFailure(error)) throw error;
    return;
  }
  const current = await db.samples.bulkGet(created.map(({ uuid }) => uuid));
  await Promise.all(
    current.map(
      async (sample, index) =>
        sample &&
        db.samples.update(sample.uuid, {
          isCreatedOffline: false,
          isUpdatedOffline: !isEqual(await sample.asForm(), forms[index]),
        })
    )
  );
};

const syncSamples = async (phases, only, blocked) => {
  const samples = only(
    "samples",
    await db.samples
      .where("phase")
      .anyOf(phases.map(({ uuid }) => uuid))
      .toArray()
  );
  const created = [];
  const forms = [];
  for (const sample of samples.filter((s) => s.isCreatedOffline)) {
    const form = await sample.asForm();
    if (referencesAny(form, blocked)) {
      markStep(recordKey("samples", sample.uuid), SYNC_STEPS.BLOCKED);
      blocked.add(sample.uuid);
      continue;
    }
    created.push(sample);
    forms.push(form);
  }
  if (created.length) await createSamples(created, forms, blocked);
  await syncUpdatedSamples(
    samples.filter(
      ({ isCreatedOffline, isUpdatedOffline }) =>
        isUpdatedOffline && !isCreatedOffline
    ),
    { blocked }
  );
};

const BIMDATA_TOKEN_REUSE_MS = 5 * 60 * 1000;
let bimdataTokenRequest = null;

const favoriteTogglesInFlight = new Set();

const LIST_PROJECT_RELATIONS = ["case", "client"];
const CURRENT_PROJECT_RELATIONS = ["case", "client", "contributors"];
const PUSHED_PROJECT_RELATIONS = [
  "case",
  "client",
  "installations",
  "recordBooks",
];
const PUSHED_INSTALLATION_RELATIONS = [
  "configurationLines",
  "sums",
  "sampleFlasks",
  "phases",
];
const INSTALLATION_TABLES = [
  "installations",
  "configurationLines",
  "sums",
  "vles",
  "flows",
  "sampleFlasks",
  "phases",
  "samples",
];

// INFO - B.L - 17/07/2023 - record books needs to be save in a certain order for fk integrity
const RECORD_BOOK_SYNC_BATCHES = [
  ["environmental_condition"],
  ["speed", "analyser", "humidity", "flow"],
  ["nozzle_diameter"],
  ["manual_sampling"],
];

const RECORD_BOOK_LINKS = [
  "environmentalCondition",
  "speed",
  "humidity",
  "nozzleDiameter",
  "manual",
];

const toRecordBookSkeleton = (form) => {
  const fields = recipeFormFields(form.type);
  return {
    ...form,
    ...Object.fromEntries(RECORD_BOOK_LINKS.map((link) => [link, null])),
    recordForm: fields ? pick(form.recordForm, fields) : form.recordForm,
  };
};

const pushRecordBooks = async (recordBooks, blocked) => {
  await createRecordBookSkeletons(recordBooks, toRecordBookSkeleton, {
    blocked,
  });
  const uuids = recordBooks.map(({ uuid }) => uuid);
  const synced = (
    await db.recordBooks.where("uuid").anyOf(uuids).toArray()
  ).filter(({ isCreatedOffline }) => !isCreatedOffline);
  for (const batch of RECORD_BOOK_SYNC_BATCHES) {
    await syncRecordBooks(
      synced.filter(({ type }) => batch.includes(type)),
      { blocked }
    );
  }
};

const pushInstallation = async (installation, { only, has, blocked }) => {
  await installation.setRelationshipsOf(PUSHED_INSTALLATION_RELATIONS);

  await syncConfigurationLines(
    only("configurationLines", installation.configurationLines),
    { blocked }
  );

  await syncSums(only("sums", installation.sums), { blocked });

  if (has("vles", "flows")) {
    const sumsUuids = new Set(installation.sums.map((sum) => sum.uuid));
    const configurationLinesUuids = new Set(
      installation.configurationLines.map((line) => line.uuid)
    );
    const vles = await db.vles
      .filter(
        ({ configurationLine, sum }) =>
          sumsUuids.has(sum) || configurationLinesUuids.has(configurationLine)
      )
      .toArray();
    await syncVles(only("vles", vles), { blocked });

    const vlesUuids = vles.map((vle) => vle.uuid);
    const flows = await db.flows.where("vle").anyOf(vlesUuids).toArray();
    await syncFlows(only("flows", flows), { blocked });
  }

  await syncPhases(only("phases", installation.phases), { blocked });
  await syncSamples(installation.phases, only, blocked);

  await syncFlasks(only("sampleFlasks", installation.sampleFlasks), {
    blocked,
  });
};

const keptAcquisitionsOf = async (projectUuid, rows) => {
  if (!rows)
    return db.acquisitionRecordBooks
      .where("project")
      .equals(projectUuid)
      .filter((row) => isPendingRecord(row) || row.isStoredOffline)
      .toArray();
  const uuids = rows
    .filter(({ table }) => table === "acquisitionRecordBooks")
    .map(({ uuid }) => uuid);
  return (await db.acquisitionRecordBooks.bulkGet(uuids)).filter(Boolean);
};

const pushProjectChanges = async (uuid, { rows, blocked = new Set() } = {}) => {
  const keys = rows && new Set(rows.map(({ key }) => key));
  const tables = rows && new Set(rows.map(({ table }) => table));
  const keep = (table, record) =>
    !keys || keys.has(recordKey(table, record.uuid));
  const only = (table, records) =>
    records.filter((record) => keep(table, record));
  const has = (...names) => !tables || names.some((name) => tables.has(name));

  let project = await db.projects.get({ uuid });
  if (keep("projects", project))
    [project] = await syncProject(project, { blocked });
  await project.setRelationshipsOf(PUSHED_PROJECT_RELATIONS);
  const { client } = project;
  if (client && keep("clients", client)) await syncClient(client, { blocked });
  if (has(...INSTALLATION_TABLES)) {
    await syncInstallations(only("installations", project.installations), {
      blocked,
    });
    for (const installation of project.installations) {
      await pushInstallation(installation, { only, has, blocked });
    }
  }

  if (has("recordBooks"))
    await pushRecordBooks(only("recordBooks", project.recordBooks), blocked);

  if (has("acquisitionRecordBooks")) {
    const acquisitions = await keptAcquisitionsOf(uuid, rows);
    await syncAcquisitionDrafts(only("acquisitionRecordBooks", acquisitions), {
      blocked,
    });
  }
  return project;
};

const isStillPending = async ({ table, uuid }) =>
  isPendingRecord(await db[table]?.get({ uuid }));

const unsentCreations = async (rows) => {
  const records = await Promise.all(
    rows.map(({ table, uuid }) => db[table]?.get({ uuid }))
  );
  return new Set(
    records.filter((record) => record?.isCreatedOffline).map(({ uuid }) => uuid)
  );
};

export const useProjectsStore = defineStore("projects", {
  state: () => ({
    currentProject: null,
    currentProjectIsLoading: false,
    projectIsLoading: false,
    projectList: [],
    totalProjects: 0,
    favorites: [],
  }),
  getters: {
    currentProjectUuid: (state) => state.currentProject?.uuid,

    currentProjectManagerEmail: (state) => state.currentProject?.managerEmail,
    isOfflineState: (state) =>
      !!state.currentProject?.isStoredOffline ||
      import.meta.env.VITE_APP_FORCE_OFFLINE === "true",
  },
  actions: {
    async setCurrentProject(uuid) {
      this.currentProjectIsLoading = true;
      const project = await db.projects.get({ uuid });
      if (!project) throw new Error("404");
      await project.setRelationshipsOf(CURRENT_PROJECT_RELATIONS);
      this.currentProject = project;
      this.currentProjectIsLoading = false;
      if (
        bimdataTokenRequest?.projectUuid !== uuid ||
        Date.now() - bimdataTokenRequest.requestedAt > BIMDATA_TOKEN_REUSE_MS
      ) {
        bimdataTokenRequest = { projectUuid: uuid, requestedAt: Date.now() };
        projectClientRest
          .retrieveBimdataToken(uuid)
          .then((token) => initBimdataAuth(token))
          .catch((error) => {
            bimdataTokenRequest = null;
            logRequestFailure(
              "No BIMData token available for this project",
              error
            );
          });
      }

      return project;
    },

    async dropLocalProject(uuid) {
      await db.projects.delete(uuid);
      if (this.currentProject?.uuid === uuid) this.currentProject = null;
      this.projectList = this.projectList.filter(
        (project) => project.uuid !== uuid
      );
    },

    async updateProject(form, setAsCurrent = true, options = {}) {
      try {
        const project = await innerUpdate(form, this.isOfflineState, options);
        if (setAsCurrent) {
          const currentProject = await db.projects.get({ uuid: project.uuid });
          await currentProject.setRelationshipsOf(CURRENT_PROJECT_RELATIONS);
          this.currentProject = currentProject;
        }
      } catch (error) {
        if (error?.response?.status !== 404) throw error;
        await this.dropLocalProject(form.uuid);
      }
    },

    async fetchProject(uuid) {
      const storedProject = await db.projects.get({ uuid });
      if (storedProject?.isStoredOffline) {
        return storedProject;
      }
      // if (!project && !window.navigator.onLine) {
      //   // TODO - B.L - 05/05/2023 - redirect to homepage
      // }
      const projectData = await projectClientRest.retrieve(uuid);
      const { case: pbCase, client } = projectData;

      if (pbCase) await db.models.Case.createOrUpdate(pbCase);

      if (client) await db.models.Client.createOrUpdate(client);

      return db.models.Project.createOrUpdate(projectData);
    },

    async syncFullProject(uuid) {
      const project = await this.fetchProject(uuid);
      if (!project.isStoredOffline) {
        if (!useRolesStore().roleList.length) {
          useRolesStore().fetchRoles({
            filters: { service_id: VITE_APP_ATMO_SERVICE_ID },
          });
        }
        useInstallationsStore().fetchInstallations(uuid);
        useSamplesStore().fetchSamples(uuid);
        usePhasesStore().fetchPhases(uuid);
        useSumStore().fetchSums(uuid);
        useVlesStore().fetchVles(uuid);
        useFlowsStore().fetchFlows(uuid);
        useDocumentsStore().fetchDocumentTypes();
        useConfigurationLinesStore().fetchConfigurationLines(uuid);
        useContributorsStore().fetchContributors(uuid);
        useSampleFlasksStore().fetchSampleFlasks(uuid);
        useSampleFlaskPrepsStore().fetchSampleFlaskPreps(uuid);
        useOrderFormsStore().fetchOrderForms(uuid);
        useRecordBooksStore().fetchRecordBooks(uuid);
        if (!useReportTemplatesStore().reportTemplateList.length) {
          useReportTemplatesStore().fetchReportTemplates(
            VITE_APP_ATMO_SERVICE_ID
          );
        }
        // INFO B.L Disabled ATM
        useAnalysisResultStore().fetchAnalysisResult(uuid);
        useAcquisitionRecordBooksStore().fetchAcquisitionRecordBook(uuid);
      }
      return db.projects.get({ uuid });
    },

    async updateFullProject(uuid) {
      if (isNetworkUnavailable()) throw new OfflineError();
      let project = null;
      try {
        const garbages = await db.garbages
          .where({ installationUuid: uuid })
          .toArray();
        await Promise.all(
          garbages.map(async (garbage) => {
            try {
              await modelToApiMapping[garbage.table].destroy(garbage.uuid);
              await db.garbages.delete(garbage.uuid);
            } catch (err) {
              if (err?.response?.status === 404) {
                await db.garbages.delete(garbage.uuid);
                return;
              }
              console.error(
                `Error during delete table ${garbage.table}, uuid: ${garbage.uuid}`
              );
              console.error(err);
            }
          })
        );
        project = await pushProjectChanges(uuid);
      } catch (err) {
        console.error(err);
        await db.projects.update(uuid, { isStoredOffline: true });
        return db.projects.get({ uuid });
      }
      project.isStoredOffline = false;
      return project;
    },

    async pushPendingChanges(uuid, { retryRejected = false } = {}) {
      const project = await db.projects.get({ uuid });
      if (project?.isStoredOffline) return;
      const pending = await pendingRecords(uuid);
      const rows = pending.filter((row) => retryRejected || !row.rejected);
      const blocked = await unsentCreations(
        pending.filter((row) => !rows.includes(row))
      );
      try {
        if (project && rows.length)
          await pushProjectChanges(uuid, { rows, blocked });
      } finally {
        const stillPending = await Promise.all(
          rows.map((row) => !!project && isStillPending(row))
        );
        await unmarkRecords(
          rows.filter((_, index) => !stillPending[index]).map(({ key }) => key)
        );
      }
    },

    async filterProjects({ queryAsObject, ordering, filterMapping }) {
      // INFO - B.L - 06/09/2022 - Lock call till app finished loading all projects
      while (this.projectIsLoading)
        await new Promise((r) => setTimeout(r, 200));

      const query = { ...queryAsObject };
      this.projectIsLoading = true;

      try {
        const { queryset, count } = await buildQueryset(
          db.projects,
          query,
          "uuid",
          filterMapping,
          true
        );
        const pageSize = query.page_size || 10;
        const start = ((query.page || 1) - 1) * pageSize;
        const projects = sortByOrdering(
          await queryset.toArray(),
          ordering
        ).slice(start, start + pageSize);
        await Promise.all(
          projects.map((project) =>
            project.setRelationshipsOf(LIST_PROJECT_RELATIONS)
          )
        );
        this.projectList = projects;
        this.totalProjects = count;
      } finally {
        this.projectIsLoading = false;
      }
    },

    async listProjects({ queryAsObject, ordering }) {
      this.projectIsLoading = true;
      try {
        const data = await projectClientRest.list({
          order_by: camelToSnakeCase(ordering),
          ...queryAsObject,
        });
        const projects = [];
        for (const result of data.results) {
          let project = await db.projects.get({ uuid: result.uuid });
          if (project?.isStoredOffline) {
            projects.push(project);
            continue;
          }
          if (result.case) {
            result.caseUuid = result.case.uuid;
            await db.models.Case.createOrUpdate(result.case);
          }

          if (result.client) {
            result.clientUuid = result.client.uuid;
            await db.models.Client.createOrUpdate(result.client);
          }

          await db.models.Project.createOrUpdate(result);
          project = await db.projects.get({ uuid: result.uuid });
          await project.setRelationshipsOf(LIST_PROJECT_RELATIONS);
          projects.push(project);
        }
        this.projectList = projects;
        this.totalProjects = data.count;
      } finally {
        this.projectIsLoading = false;
      }
    },

    async searchProject(searchText) {
      if (isNetworkUnavailable()) {
        throw new OfflineError();
      }
      return db.projects
        .filter(
          (item) =>
            item.projectName.toLowerCase().includes(searchText.toLowerCase()) ||
            item.chronorapso.toLowerCase().includes(searchText.toLowerCase())
        )
        .toArray();
    },

    async archiveOrUnarchiveProject(project) {
      if (isNetworkUnavailable()) {
        throw new OfflineError();
      }
      const response =
        await projectClientRest.archiveOrUnarchiveProject(project);
      project.isArchived = response.isArchived;
      this.replaceProject(project);
      await db.projects.update(project.uuid, {
        isArchived: response.isArchived,
      });
      return project;
    },

    replaceProject(project) {
      const copy = [...this.projectList];
      const index = copy.findIndex((item) => project.uuid === item.uuid);
      if (index < 0) return;
      copy[index] = project;
      this.projectList = copy;
    },

    async fetchFavorites() {
      if (isNetworkUnavailable()) {
        throw new OfflineError();
      }
      const data = await projectClientRest.listFavorite();
      this.favorites = data.map((item) => ({
        name: item.projectName,
        uuid: item.uuid,
      }));
    },

    async toggleFavorite(project) {
      if (favoriteTogglesInFlight.has(project.uuid)) return;
      if (isNetworkUnavailable()) {
        throw new OfflineError();
      }
      favoriteTogglesInFlight.add(project.uuid);
      try {
        await projectClientRest.toggleFavorite(project.uuid);
        const index = this.favorites.findIndex(
          (item) => item.uuid === project.uuid
        );
        if (index !== -1) {
          this.favorites.splice(index, 1);
        } else {
          this.favorites.push({
            name: project.projectName,
            uuid: project.uuid,
          });
        }
      } finally {
        favoriteTogglesInFlight.delete(project.uuid);
      }
    },

    async changeProjectManager(projectUuid) {
      if (isNetworkUnavailable()) {
        throw new OfflineError();
      }
      return projectClientRest.changeProjectManager(projectUuid);
    },
  },
});
